// ===========================================
// Who can see an event: staff-facing sentences
// ===========================================
// ONE set of words for "who sees this event", shared by the admin event page's "View public page"
// button (why there is no public page) and the admin Preview banner (what the public / families
// actually get). The rules they describe are the server's:
//   - the public page serves PUBLISHED + isPublic events only (`getPublicEventBySlug`);
//   - a family reads a PUBLISHED event shown to it, or a CANCELLED / COMPLETED one it was going to,
//     and never a STAFF_ONLY, DRAFT or ARCHIVED one (`familyReadableEventWhere`).
// Labels follow the event editor ("Public", "Staff Only").

import type { EventStatus, EventVisibility } from '../../api/events.api';

const ONLY_PUBLIC_HAS_A_PAGE = 'Only events set to Public have a public page.';
const ALL_FAMILIES_SEE = 'All families see this event when they log in.';
const SOME_FAMILIES_SEE = 'The families it’s shown to see it when they log in.';

/** Why a non-public event has no public page. */
export function notPublicReason(visibility: EventVisibility): string {
  switch (visibility) {
    case 'STAFF_ONLY':
      return 'This event is Staff Only, so it has no public page and families can’t see it.';
    case 'ALL_FAMILIES':
      return `${ONLY_PUBLIC_HAS_A_PAGE} ${ALL_FAMILIES_SEE}`;
    case 'PUBLIC':
      return ONLY_PUBLIC_HAS_A_PAGE;
    default:
      return `${ONLY_PUBLIC_HAS_A_PAGE} ${SOME_FAMILIES_SEE}`;
  }
}

/** Why a public event's page isn't showing, by status (only a draft can still be published). */
export const PUBLIC_PAGE_STATUS_REASON: Record<Exclude<EventStatus, 'PUBLISHED'>, string> = {
  DRAFT: 'The public page appears once the event is published.',
  CANCELLED: 'This event is cancelled, so its public page is no longer shown.',
  COMPLETED: 'This event is completed, so its public page is no longer shown.',
  ARCHIVED: 'This event is archived, so its public page is no longer shown.',
};

/** Which families see a PUBLISHED event, by its visibility. Public events show to every family too. */
export function familyAudience(visibility: EventVisibility): string {
  switch (visibility) {
    case 'STAFF_ONLY':
      return 'This event is Staff Only, so families can’t see it.';
    case 'ALL_FAMILIES':
    case 'PUBLIC':
      return ALL_FAMILIES_SEE;
    default:
      return SOME_FAMILIES_SEE;
  }
}

/** What the families still going to a closed event see (`FAMILY_READABLE_EVENT_STATUSES`). */
const GOING_ONLY = 'only families who were going (an RSVP or a ticket) can still open this event';

/**
 * The admin Preview banner's notes: what the chosen audience ACTUALLY gets for this event, which can
 * be nothing at all. Empty = the preview is exactly what they see.
 */
export function previewAudienceNotes(
  view: 'public' | 'family',
  status: EventStatus,
  visibility: EventVisibility | undefined,
  isPublic: boolean
): string[] {
  if (view === 'public') {
    const notes: string[] = [];
    if (!isPublic && visibility) notes.push(notPublicReason(visibility));
    if (status === 'DRAFT') {
      notes.push(`${PUBLIC_PAGE_STATUS_REASON.DRAFT} Until then, visitors to its link get “Event not found”.`);
    } else if (status !== 'PUBLISHED') {
      notes.push(`${PUBLIC_PAGE_STATUS_REASON[status]} Visitors to its link get “Event not found”.`);
    }
    return notes;
  }

  // Families never see a Staff Only event, whatever its status.
  if (visibility === 'STAFF_ONLY') return [familyAudience('STAFF_ONLY')];
  switch (status) {
    case 'PUBLISHED':
      return visibility ? [familyAudience(visibility)] : [];
    case 'DRAFT':
      return [
        'Draft: families can’t see this event until it is published.',
        ...(visibility ? [`Once it is: ${familyAudience(visibility)}`] : []),
      ];
    case 'CANCELLED':
      return [`Cancelled: ${GOING_ONLY}, and they see it as cancelled.`];
    case 'COMPLETED':
      return [`Completed: ${GOING_ONLY}, and they see it as finished.`];
    case 'ARCHIVED':
      return ['Archived: families can’t see this event.'];
    default:
      return [];
  }
}
